import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import {
  type IManufacturer,
  type IManufacturerInput,
  type IProductBuyer,
  type IProductClaimAcceptanceInput,
  type IProductClaimHit,
  type IProductSafetyGapRow,
  type IProductSafetyUpdate,
  type IResponsiblePerson,
  type IResponsiblePersonInput,
} from '@mahawi/eshop-common/src/types';
import { createSlice } from '@reduxjs/toolkit';

import {
  asyncStateInitial,
  finishLoading,
  requestFailedMatcher,
  resetLoadingOnRequestFailed,
  startLoading,
} from '../async-state';
import {
  type IManufacturerSaveResult,
  type IProductBuyersRequest,
  type IProductSafetyDocumentDeleteRequest,
  type IProductSafetyResult,
  type IProductSafetyState,
  type IResponsiblePersonSaveResult,
} from './types';

export const preloadedState: IProductSafetyState = {
  ...asyncStateInitial,
  manufacturers: undefined,
  responsiblePersons: undefined,
  validationErrors: [],
  productSafety: undefined,
  failure: null,
  buyers: undefined,
  gapRows: undefined,
  claimHits: undefined,
};

const byName = <T extends { name: string }>(items: T[]): T[] =>
  [...items].sort((a: T, b: T): number => stringCompare(a.name, b.name));

const upsertByUuid = <T extends { uuid: string; name: string }>(
  items: T[] | undefined,
  item: T,
): T[] =>
  byName([
    ...(items ?? []).filter(
      (existing: T): boolean => existing.uuid !== item.uuid,
    ),
    item,
  ]);

const productSafetySlice = createSlice({
  name: 'ProductSafety',
  initialState: preloadedState,
  reducers: {
    manufacturersLoadAll(state) {
      startLoading(state);
    },
    manufacturersLoadAllResponse: {
      reducer(state, action: { payload: { manufacturers: IManufacturer[] } }) {
        state.manufacturers = byName(action.payload.manufacturers);
        finishLoading(state);
      },
      prepare(manufacturers: IManufacturer[]) {
        return { payload: { manufacturers } };
      },
    },
    manufacturerSave: {
      reducer(state) {
        startLoading(state);
        state.validationErrors = [];
      },
      prepare(manufacturer: IManufacturerInput) {
        return { payload: { manufacturer } };
      },
    },
    manufacturerSaveResponse: {
      reducer(state, action: { payload: IManufacturerSaveResult }) {
        state.validationErrors = action.payload.errors;

        if (action.payload.manufacturer) {
          state.manufacturers = upsertByUuid(
            state.manufacturers,
            action.payload.manufacturer,
          );
        }

        finishLoading(state);
      },
      prepare(result: IManufacturerSaveResult) {
        return { payload: result };
      },
    },
    responsiblePersonsLoadAll(state) {
      startLoading(state);
    },
    responsiblePersonsLoadAllResponse: {
      reducer(
        state,
        action: { payload: { responsiblePersons: IResponsiblePerson[] } },
      ) {
        state.responsiblePersons = byName(action.payload.responsiblePersons);
        finishLoading(state);
      },
      prepare(responsiblePersons: IResponsiblePerson[]) {
        return { payload: { responsiblePersons } };
      },
    },
    responsiblePersonSave: {
      reducer(state) {
        startLoading(state);
        state.validationErrors = [];
      },
      prepare(responsiblePerson: IResponsiblePersonInput) {
        return { payload: { responsiblePerson } };
      },
    },
    responsiblePersonSaveResponse: {
      reducer(state, action: { payload: IResponsiblePersonSaveResult }) {
        state.validationErrors = action.payload.errors;

        if (action.payload.responsiblePerson) {
          state.responsiblePersons = upsertByUuid(
            state.responsiblePersons,
            action.payload.responsiblePerson,
          );
        }

        finishLoading(state);
      },
      prepare(result: IResponsiblePersonSaveResult) {
        return { payload: result };
      },
    },
    productSafetyLoad: {
      reducer(state) {
        startLoading(state);
        state.productSafety = undefined;
        state.failure = null;
      },
      prepare(productUuid: string) {
        return { payload: { productUuid } };
      },
    },
    productSafetyUpdate: {
      reducer(state) {
        startLoading(state);
        state.failure = null;
      },
      prepare(update: IProductSafetyUpdate) {
        return { payload: { update } };
      },
    },
    productSafetyDocumentDelete: {
      reducer(state) {
        startLoading(state);
        state.failure = null;
      },
      prepare(request: IProductSafetyDocumentDeleteRequest) {
        return { payload: request };
      },
    },
    productSafetyResponse: {
      reducer(state, action: { payload: IProductSafetyResult }) {
        if (action.payload.productSafety) {
          state.productSafety = action.payload.productSafety;
        }

        state.failure = action.payload.failure;
        finishLoading(state);
      },
      prepare(result: IProductSafetyResult) {
        return { payload: result };
      },
    },
    productBuyersLoad: {
      reducer(state) {
        startLoading(state);
        state.buyers = undefined;
      },
      prepare(request: IProductBuyersRequest) {
        return { payload: request };
      },
    },
    productBuyersLoadResponse: {
      reducer(state, action: { payload: { buyers: IProductBuyer[] } }) {
        state.buyers = action.payload.buyers;
        finishLoading(state);
      },
      prepare(buyers: IProductBuyer[]) {
        return { payload: { buyers } };
      },
    },
    reportProductSafetyLoad(state) {
      startLoading(state);
    },
    reportProductClaimsLoad: {
      reducer(state) {
        startLoading(state);
      },
      prepare(languageCode?: string) {
        return { payload: { languageCode } };
      },
    },
    reportProductClaimsLoadResponse: {
      reducer(state, action: { payload: { hits: IProductClaimHit[] } }) {
        state.claimHits = action.payload.hits;
        finishLoading(state);
      },
      prepare(hits: IProductClaimHit[]) {
        return { payload: { hits } };
      },
    },
    productClaimAccept: {
      reducer(state) {
        startLoading(state);
        state.failure = null;
      },
      prepare(acceptance: IProductClaimAcceptanceInput) {
        return { payload: { acceptance } };
      },
    },
    reportProductSafetyLoadResponse: {
      reducer(state, action: { payload: { rows: IProductSafetyGapRow[] } }) {
        state.gapRows = action.payload.rows;
        finishLoading(state);
      },
      prepare(rows: IProductSafetyGapRow[]) {
        return { payload: { rows } };
      },
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      requestFailedMatcher('ProductSafety'),
      resetLoadingOnRequestFailed,
    );
  },
});

export const {
  manufacturersLoadAll,
  manufacturersLoadAllResponse,
  manufacturerSave,
  manufacturerSaveResponse,
  responsiblePersonsLoadAll,
  responsiblePersonsLoadAllResponse,
  responsiblePersonSave,
  responsiblePersonSaveResponse,
  productSafetyLoad,
  productSafetyUpdate,
  productSafetyDocumentDelete,
  productSafetyResponse,
  productBuyersLoad,
  productBuyersLoadResponse,
  reportProductSafetyLoad,
  reportProductSafetyLoadResponse,
  reportProductClaimsLoad,
  reportProductClaimsLoadResponse,
  productClaimAccept,
} = productSafetySlice.actions;
export default productSafetySlice.reducer;
